import * as React from "react";
import BaseModel from "../../models/BaseModel";
import WatchableValue from "../../models/WatchableValue";
import { useRedraw } from "../../models/useRerender";
import ObjectReference, { ShowNameMode } from "../ObjectReference";
import type { ColumnConfig, AdditionalColumnConfig } from "./UniversalObjectTable";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";

export type UniversalObjectTableRowProps = {
  obj: BaseModel;
  selectedObjects?: WatchableValue<Set<BaseModel>>;
  onSelectionChange?: () => void;
  additionalColumns?: AdditionalColumnConfig[];
  grandparentColumnConfig?: ColumnConfig;
  parentColumnConfig?: ColumnConfig;
  objectColumnConfig?: ColumnConfig;
  onUnlink?: (obj: BaseModel | undefined) => void;
};

type AdditionalColumnUIProps = {
  column: AdditionalColumnConfig;
  index: number;
  obj: BaseModel;
};

function AdditionalColumnUI(props: AdditionalColumnUIProps) {
  const { column, index, obj } = props;

  return (
    <div className={`acol-${index}`}>
      {column.objProperty && <div style={{ paddingLeft: "10px", paddingRight: "10px" }}>{obj[column.objProperty]}</div>}

      {column.element && column.element(obj)}

      {column.columnConfig.getObject && (
        <ObjectReference
          obj={column.columnConfig.getObject(obj)}
          showFullTag={column.columnConfig.showFullTag}
          showTag={column.columnConfig.showTag}
          showName={column.columnConfig.showName}
        />
      )}
    </div>
  );
}

export default function UniversalObjectTableRow(props: UniversalObjectTableRowProps) {
  const {
    obj,
    selectedObjects,
    onSelectionChange,
    additionalColumns,
    grandparentColumnConfig,
    parentColumnConfig,
    objectColumnConfig,
    onUnlink,
  } = props;
  const redraw = useRedraw();
  useOnChangeWatcher(selectedObjects);
  useOnChangeWatcher(obj);

  const showCheckboxes = selectedObjects != null;
  const showGrandparent = grandparentColumnConfig != null;
  const showParent = parentColumnConfig != null;

  let showNameGrandparent: ShowNameMode = grandparentColumnConfig?.filter.filterExpression ? "always" : "onHover"; // no itx
  let showNameParent: ShowNameMode = parentColumnConfig?.filter.filterExpression ? "always" : "onHover"; // no itx
  let showNameObject: ShowNameMode = onUnlink != null ? "afterButtons" : "always"; // no itx

  if (objectColumnConfig?.showName !== undefined) {
    showNameObject = objectColumnConfig.showName as ShowNameMode;
  }

  console.log("showNameObject", obj?.tag, obj?.name, showNameObject);

  let showTagObject = !obj.isButtonChannel;

  if (objectColumnConfig?.showTag !== undefined) {
    showTagObject = objectColumnConfig.showTag;
  }

  return (
    <div
      className="UniversalObjectTableRow"
      style={{
        cursor: "default",
        display: "flex",
        flexDirection: "row",
        whiteSpace: "nowrap",
        height: "35px",
        alignItems: "center",
        width: "100%",
      }}
    >
      {showCheckboxes && selectedObjects != null && (
        <div
          style={{
            height: "35px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <input
            type="checkbox"
            checked={selectedObjects.value.has(obj)}
            style={{ height: "15px" }}
            id={`checkbox_${obj.id}`}
            onChange={(e) => {
              const set = selectedObjects.value;
              if (e.target.checked) {
                set.add(obj);
              } else {
                set.delete(obj);
              }
              selectedObjects.value = new Set(set);
              onSelectionChange?.();
              redraw();
            }}
          />
        </div>
      )}

      {additionalColumns?.map((column, index) =>
        column.position == "left" ? <AdditionalColumnUI key={index} column={column} index={index} obj={obj} /> : null,
      )}

      {showGrandparent && obj.parent?.parent && grandparentColumnConfig && (
        <ObjectReference
          className="col-grandparent"
          useAllwaysMaxSpace={true}
          obj={obj.parent.parent as BaseModel}
          showIcon={grandparentColumnConfig.showIcon ?? true}
          showTag={grandparentColumnConfig.showTag ?? true}
          showFullTag={true}
          showName={showNameGrandparent}
          style={{ paddingRight: "0px" }}
        />
      )}
      {showParent && obj.parent && parentColumnConfig && (
        <ObjectReference
          className="col-parent"
          useAllwaysMaxSpace={true}
          obj={obj.parent as BaseModel}
          showIcon={parentColumnConfig.showIcon ?? true}
          showTag={parentColumnConfig.showTag ?? true}
          showFullTag={true}
          showName={showNameParent}
          style={{ paddingRight: "0px" }}
        />
      )}

      <ObjectReference
        obj={obj}
        className="col-object"
        useAllwaysMaxSpace={true}
        showIcon={objectColumnConfig?.showIcon ?? true}
        showTag={showTagObject}
        showFullTag={false}
        showName={showNameObject}
        onUnlink={onUnlink ? () => onUnlink(obj) : undefined}
        style={{ paddingRight: "0px" }}
      />

      {additionalColumns?.map((column, index) =>
        column.position == "right" ? <AdditionalColumnUI key={index} column={column} index={index} obj={obj} /> : null,
      )}
    </div>
  );
}
